import type {
  ActiveScan,
  RelationalIntroductionLetterResponse,
} from '@/models/Scans'
import { throwIfProfileRegenerating } from './profileRegenError'

const serverURL = import.meta.env.VITE_API_URL

export async function runScan(
  token: string,
  funding_need_id: number,
  include_relational: boolean,
  include_future_planning: boolean = false,
  is_onboarding: boolean = false,
): Promise<{ job_id: string; status: string }> {
  const response = await fetch(`${serverURL}/scans/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      need_id: funding_need_id,
      include_relational,
      include_future_planning,
      is_onboarding,
    }),
  })

  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    if (response.status === 402) {
      const errorData = await response.json()
      const error = new Error(
        errorData.detail || 'Upgrade required to access this feature',
      )
      error.name = 'PaymentRequired'
      throw error
    }
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  // The API returns a job_id and status to track progress
  const data = await response.json()
  return data
}

export async function fetchScanIdsByNeedId(
  token: string,
  funding_need_id: number,
) {
  const response = await fetch(
    `${serverURL}/scans/funding-need/${funding_need_id}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data
}
export async function fetchOrgScanUsage(token: string, org_uuid: string) {
  const response = await fetch(`${serverURL}/scans/org-usage/${org_uuid}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data as { total_scans_used: number }
}

export async function fetchLatestScanResultsByNeedId(
  token: string,
  funding_need_id: number,
  showHiddenMatches: boolean = false,
) {
  const url = new URL(`${serverURL}/scans/latest/${funding_need_id}`)
  if (showHiddenMatches) {
    url.searchParams.append('show_hidden', 'true')
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data
}

export async function fetchActiveScanForNeed(
  token: string,
  funding_need_id: number,
): Promise<ActiveScan | null> {
  const response = await fetch(
    `${serverURL}/scans/active/${funding_need_id}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

export async function fetchScanResultsByScanIds(
  token: string,
  scanIds: number[],
) {
  const response = await fetch(`${serverURL}/scans/scan-ids`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(scanIds),
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

export async function addRemoveFavourite(
  token: string,
  scanId: number,
  currentMark: boolean,
) {
  // mark_as: true to mark the scan result as favourite, false to remove it from favourites
  const response = await fetch(`${serverURL}/scans/favourite/${scanId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ mark_as: !currentMark }),
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response
}

export async function generateRelationalIntroductionLetter(
  token: string,
  scanId: number,
): Promise<RelationalIntroductionLetterResponse> {
  const response = await fetch(
    `${serverURL}/scans/${scanId}/generate-relational-introduction-letter`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    if (response.status === 402) {
      throw new Error(
        'Free trial limit reached for relational introduction letters',
      )
    }
    if (response.status === 403 || response.status === 409) {
      const errorData = await response.json().catch(() => null)
      throw new Error(
        typeof errorData?.detail === 'string'
          ? errorData.detail
          : 'Failed to generate relational introduction letter',
      )
    }
    throw new Error('Failed to generate relational introduction letter')
  }

  return response.json()
}

export async function fetchLatestRelationalIntroductionLetter(
  token: string,
  scanId: number,
): Promise<RelationalIntroductionLetterResponse | null> {
  const response = await fetch(
    `${serverURL}/scans/${scanId}/relational-introduction-letter`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (response.status === 404) return null
  if (!response.ok) {
    throw new Error('Failed to load relational introduction letter')
  }

  return response.json()
}

export async function saveRelationalIntroductionLetter(
  token: string,
  scanId: number,
  content: string,
): Promise<RelationalIntroductionLetterResponse> {
  const response = await fetch(
    `${serverURL}/scans/${scanId}/relational-introduction-letter/save`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content }),
    },
  )

  if (!response.ok) {
    throw new Error('Failed to save relational introduction letter')
  }

  return response.json()
}

export async function markRelationalIntroductionLetterDone(
  token: string,
  scanId: number,
  content: string,
): Promise<RelationalIntroductionLetterResponse> {
  const response = await fetch(
    `${serverURL}/scans/${scanId}/relational-introduction-letter/mark-done`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content }),
    },
  )

  if (!response.ok) {
    throw new Error('Failed to mark relational introduction letter as done')
  }

  return response.json()
}

export async function regenerateRelationalIntroductionLetter(
  token: string,
  scanId: number,
  feedback: string,
): Promise<RelationalIntroductionLetterResponse> {
  const response = await fetch(
    `${serverURL}/scans/${scanId}/regenerate-relational-introduction-letter`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ feedback }),
    },
  )

  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    if (response.status === 403) {
      const errorData = await response.json().catch(() => null)
      throw new Error(
        typeof errorData?.detail === 'string'
          ? errorData.detail
          : 'Failed to regenerate relational introduction letter',
      )
    }
    throw new Error('Failed to regenerate relational introduction letter')
  }

  return response.json()
}
